/**
 * Review detail side panel — slide-in from the right.
 * Shows full review text, media, product/order links, approve/reject actions,
 * seller reply compose, and delete (in overflow menu).
 */

import { useState, useEffect, useRef } from "react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Check,
  X,
  MoreHorizontal,
  Trash2,
  ExternalLink,
  Image as ImageIcon,
  Clock,
  Send,
  Pencil,
} from "lucide-react"
import { Link } from "react-router-dom"
import { StarRating } from "./StarRating"
import { ReviewStatusBadge } from "./ReviewStatusBadge"
import {
  useModerateReview,
  useReplyToReview,
  useDeleteReply,
  useDeleteReview,
} from "../api/reviewHooks"
import type { ReviewItem } from "../api/reviewTypes"

interface ReviewDetailPanelProps {
  review: ReviewItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export function ReviewDetailPanel({
  review,
  open,
  onOpenChange,
}: ReviewDetailPanelProps) {
  const [replyText, setReplyText] = useState("")
  const [isEditingReply, setIsEditingReply] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteReplyDialogOpen, setDeleteReplyDialogOpen] = useState(false)
  const [expandedImage, setExpandedImage] = useState<string | null>(null)
  const lightboxRef = useRef<HTMLDivElement>(null)

  // Reset reply compose/edit state when switching between reviews
  useEffect(() => {
    setReplyText("")
    setIsEditingReply(false)
    setDeleteReplyDialogOpen(false)
  }, [review?.name])

  // Auto-focus lightbox overlay for keyboard dismissal
  useEffect(() => {
    if (expandedImage) {
      lightboxRef.current?.focus()
    }
  }, [expandedImage])

  const moderateMutation = useModerateReview()
  const replyMutation = useReplyToReview()
  const deleteReplyMutation = useDeleteReply()
  const deleteMutation = useDeleteReview()

  const handleModerate = (action: "approve" | "reject") => {
    if (!review) return
    moderateMutation.mutate(
      { reviewName: review.name, action },
      { onSuccess: () => onOpenChange(false) },
    )
  }

  // Post a new reply OR save an edit — both call admin_reply (overwrites).
  const handleReply = () => {
    if (!review || !replyText.trim()) return
    replyMutation.mutate(
      { reviewName: review.name, replyText: replyText.trim() },
      {
        onSuccess: () => {
          setReplyText("")
          setIsEditingReply(false)
        },
      },
    )
  }

  const handleStartEditReply = () => {
    if (!review) return
    setReplyText(review.seller_reply ?? "")
    setIsEditingReply(true)
  }

  const handleCancelEditReply = () => {
    setIsEditingReply(false)
    setReplyText("")
  }

  const handleDeleteReply = () => {
    if (!review) return
    deleteReplyMutation.mutate(review.name, {
      onSuccess: () => {
        setDeleteReplyDialogOpen(false)
        setIsEditingReply(false)
        setReplyText("")
      },
    })
  }

  const handleDelete = () => {
    if (!review) return
    deleteMutation.mutate(review.name, {
      onSuccess: () => {
        setDeleteDialogOpen(false)
        onOpenChange(false)
      },
    })
  }

  if (!review) return null

  const mediaItems = review.media ?? []
  const displayName = review.is_anonymous
    ? "Anonymous Buyer"
    : review.customer_name

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-base">Review Detail</SheetTitle>
                <SheetDescription className="sr-only">
                  Review by {displayName}
                </SheetDescription>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Review
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </SheetHeader>

          <div className="px-6 pb-6 space-y-6">
            {/* Product info */}
            <div className="flex items-center gap-3">
              {review.item_image ? (
                <img
                  src={review.item_image}
                  alt={review.item_name}
                  className="w-12 h-12 rounded-lg object-cover bg-muted"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <Link
                  to={`/inventory/item/${encodeURIComponent(review.item_code)}`}
                  className="text-sm font-medium hover:underline truncate block"
                >
                  {review.item_name}
                </Link>
                <Link
                  to={`/orders/${encodeURIComponent(review.order_name)}`}
                  className="text-xs text-muted-foreground hover:underline flex items-center gap-1"
                >
                  {review.order_name}
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              <ReviewStatusBadge status={review.status} />
            </div>

            {/* Rating + reviewer */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <StarRating rating={review.rating} size={18} />
                <span className="text-sm text-muted-foreground">
                  {displayName}
                </span>
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {review.review_text || "(No text)"}
              </p>
            </div>

            {/* Media grid */}
            {mediaItems.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {mediaItems.map((media, i) => (
                  <button
                    key={i}
                    className="aspect-square rounded-lg overflow-hidden bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                    onClick={() => setExpandedImage(media.url)}
                  >
                    <img
                      src={media.thumbnail_url || media.url}
                      alt={`Review media ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Timeline */}
            <div className="space-y-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" />
                <span>Submitted {formatDate(review.creation)}</span>
              </div>
              {review.status !== "Pending" && (
                <div className="flex items-center gap-2">
                  {review.status === "Approved" ? (
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  )}
                  <span>
                    {review.status} {formatDate(review.modified)}
                  </span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            {review.status === "Pending" && (
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => handleModerate("approve")}
                  disabled={moderateMutation.isPending}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Check className="mr-2 h-4 w-4" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleModerate("reject")}
                  disabled={moderateMutation.isPending}
                  className="flex-1 border-destructive/30 text-destructive hover:bg-destructive/10"
                >
                  <X className="mr-2 h-4 w-4" />
                  Reject
                </Button>
              </div>
            )}

            {/* Seller reply section */}
            <div className="space-y-3 border-t pt-4">
              <h4 className="text-sm font-medium">Seller Reply</h4>
              {review.seller_reply && !isEditingReply ? (
                <div className="rounded-lg bg-muted/50 p-3 space-y-2">
                  <p className="text-sm whitespace-pre-wrap">
                    {review.seller_reply}
                  </p>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground min-w-0 truncate">
                      {review.seller_reply_by}
                      {review.seller_reply_date &&
                        ` - ${formatDate(review.seller_reply_date)}`}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={handleStartEditReply}
                      >
                        <Pencil className="mr-1.5 h-3.5 w-3.5" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-destructive hover:text-destructive"
                        onClick={() => setDeleteReplyDialogOpen(true)}
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                    placeholder="Write a public reply..."
                    className="min-h-[80px] resize-none"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {replyText.length}/500
                    </span>
                    <div className="flex items-center gap-2">
                      {isEditingReply && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEditReply}
                          disabled={replyMutation.isPending}
                        >
                          Cancel
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={handleReply}
                        disabled={!replyText.trim() || replyMutation.isPending}
                      >
                        <Send className="mr-2 h-3.5 w-3.5" />
                        {isEditingReply ? "Save Reply" : "Post Reply"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Image lightbox */}
      {expandedImage && (
        <div
          ref={lightboxRef}
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-8"
          onClick={() => setExpandedImage(null)}
          onKeyDown={(e) => { if (e.key === "Escape") setExpandedImage(null) }}
          tabIndex={0}
          role="dialog"
          aria-label="Image preview"
        >
          <img
            src={expandedImage}
            alt="Expanded review media"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Review</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The review will be permanently
              removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete reply confirmation (does NOT close the panel) */}
      <AlertDialog
        open={deleteReplyDialogOpen}
        onOpenChange={setDeleteReplyDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reply</AlertDialogTitle>
            <AlertDialogDescription>
              This removes your public reply from the review. You can post a new
              reply afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteReply}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteReplyMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
